import React from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import LoginButton from './components/auth/LoginButton';
import NotificationBell from './components/common/NotificationBell';
import HomePage from './pages/HomePage';
import AnalysisPage from './pages/AnalysisPage';
import ProfilePage from './pages/ProfilePage';
import PricingPage from './pages/PricingPage';
import TrendingPage from './pages/TrendingPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import UserProfilePage from './pages/UserProfilePage';
import ExplorePage from './pages/ExplorePage';
import ProApplicationPage from './pages/ProApplicationPage';
import ContactPage from './pages/ContactPage';
import LegalPage from './pages/LegalPage';
import PrivacyPage from './pages/PrivacyPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

function NavBar() {
  return (
    <header className="border-b bg-white sticky top-0 z-40">
      <nav className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="text-xl font-bold text-gray-900 shrink-0">
          🀄 みんなで何切る
        </Link>
        <div className="flex items-center gap-1 sm:gap-3">
          <Link
            to="/"
            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span className="hidden sm:inline">投稿</span>
          </Link>
          <NavLink
            to="/explore"
            className={({ isActive }) =>
              `flex items-center gap-1 text-sm px-1 ${isActive ? 'text-blue-600 font-medium' : 'text-gray-600 hover:text-gray-900'}`
            }
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <span className="hidden sm:inline">探す</span>
          </NavLink>
          <NavLink
            to="/trending"
            className={({ isActive }) =>
              `flex items-center gap-1 text-sm px-1 ${isActive ? 'text-blue-600 font-medium' : 'text-gray-600 hover:text-gray-900'}`
            }
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 19h20v2H2v-2zM2 6l5 3.5L12 2l5 7.5 5-3.5v11H2V6z" />
            </svg>
            <span className="hidden sm:inline">ランキング</span>
          </NavLink>
          <NotificationBell />
          <LoginButton />
        </div>
      </nav>
    </header>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-gray-50 flex flex-col">
            <NavBar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/explore" element={<ExplorePage />} />
                <Route path="/hands/:id" element={<AnalysisPage />} />
                <Route path="/trending" element={<TrendingPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/users/:username" element={<UserProfilePage />} />
                <Route path="/apply-pro" element={<ProApplicationPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/legal" element={<LegalPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
              </Routes>
            </main>
            <footer className="border-t bg-white mt-8 py-4">
              <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-6 text-xs text-gray-400">
                <Link to="/contact" className="hover:text-gray-600">要望・お問い合わせ</Link>
                <Link to="/pricing" className="hover:text-gray-600">プラン</Link>
                <Link to="/legal" className="hover:text-gray-600">特定商取引法に基づく表記</Link>
                <Link to="/privacy" className="hover:text-gray-600">プライバシーポリシー</Link>
              </div>
            </footer>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
